import { Component, OnInit, Input, Inject, Optional } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ImageComponent } from 'tmna-mqcs-ui/src/lib/components/image';
import { ButtonComponent } from 'tmna-mqcs-ui/src/lib/components/button';
import { FileUploadModule } from 'primeng/fileupload';

import { FileCategoryModel, FileModel } from 'src/app/models/uploadDocumentModel';
import { MfgappsEventService } from 'src/app/services/mfgapps-event.service';
import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';

@Component({
  selector: 'app-upload-documents',
  imports: [MatIconModule, FileUploadModule, ButtonComponent, ImageComponent, MatProgressSpinnerModule],
  templateUrl: './upload-documents.component.html',
  styleUrl: './upload-documents.component.scss'
})
export class UploadDocumentsComponent implements OnInit {
  @Input() inputPayload: any;
  readonly maxUploadFileSize = { mb: 100, bytes: 104857600 };
  readonly maxDirectUploadSize = 6291456; //in bytes
  uploadDocumentsConfiguration: FileCategoryModel[] = [];
  showContainer = true;
  loader = { isLoading: true, message: 'Loading Images and Documents...' };
  errorMessage = "";

  constructor(@Optional() private readonly dialogRef: MatDialogRef<UploadDocumentsComponent>,
    @Inject(MAT_DIALOG_DATA) @Optional() public payload: any, private readonly mfgAppsApiService: MfgappsApiService,
    private readonly eventService: MfgappsEventService) {
  }

  ngOnInit() {
    this.payload = Object.freeze(this.payload ?? this.inputPayload);
    this.loadImagesContainer();
  }

  async loadImagesContainer() {
    try {
      this.uploadDocumentsConfiguration = await this.eventService.getFileCategoryConfiguration();
      if (this.payload.isEditMode || this.payload.isViewMode) {
        const uploadedFiles: FileModel[] = (await this.mfgAppsApiService.getEntryFiles(this.payload.entryId)).result ?? [];
        this.showContainer = !this.payload.isViewMode || uploadedFiles.length > 0;
        this.eventService.assignFilesToCategories(uploadedFiles, this.uploadDocumentsConfiguration);
      }
    }
    catch (error: any) {
      console.info(error);
      this.errorMessage = `Error while loading files, Please try again!`;
    }
    finally {
      this.resetLoader();
    }
  }

  cancel() {
    //close the dialog on cancel
    this.dialogRef?.close();
  }

  async onFileSelect(event: any, category: FileCategoryModel) {
    const uploadFile: File = event.currentFiles?.[0];
    if (!uploadFile) {
      return;
    }
    this.loader.message = `Uploading File...`;
    try {
      if (uploadFile.size > this.maxUploadFileSize.bytes) {
        this.eventService.showSnackBar(`Uploaded Document Size should not exceed ${this.maxUploadFileSize.mb} MB !`, 'error');
        return;
      }
      if (category.uploaded_files.length >= category.max_file_count) {
        this.eventService.showSnackBar(`Maximum ${category.max_file_count} files can be uploaded under ${category.category_name} !`, 'error');
        return;
      }
      await this.processSelectedFile(uploadFile, category);
    }
    catch (error: any) {
      console.info(error);
      this.eventService.showSnackBar(`Error occurred while uploading '${uploadFile.name}' !`, 'error');
    }
    this.resetLoader();
  }

  async processSelectedFile(uploadFile: File, category: FileCategoryModel) {
    try {
      const filePayload: FileModel = {
        file_name: uploadFile.name,
        file_kb_size: Math.ceil(uploadFile.size / 1024),
        file_last_modified: uploadFile.lastModified,
        isLoading: true,
        fileType: uploadFile.type.includes('image') ? 'Image' : 'Document',
        category_id: category.category_id,
        entry_id: this.payload.entryId,
        // set file order as last file's order + 1
        file_order: (category.uploaded_files.at(-1)?.file_order ?? 0) + 1
      };
      if (filePayload.fileType === 'Image') {
        const compressedFile = await this.compressImage(uploadFile);
        filePayload.file_thumbnail_body = await this.eventService.encodeToBase64(compressedFile);
      }
      const fileUploadResult = await this.uploadFile(filePayload, uploadFile);
      //Store the file id from upload API response
      filePayload.id = fileUploadResult.id;
      //Upload file on presigned URL if available in response
      if (fileUploadResult.url) {
        const uploadResult = await this.mfgAppsApiService.uploadFileOnPresignedUrl(uploadFile, fileUploadResult.url);
        if (uploadResult) {
          //To-do call 2nd API to update DB that file upload is completed successfully
        }
      }
      if (filePayload.fileType === 'Image' && filePayload.file_thumbnail_body) {
        filePayload.file_thumbnail_URL = this.eventService.thumbnailToBlobUrl(filePayload.file_thumbnail_body, filePayload.file_name);
      }
      //Push the new file to respective category list after successful upload
      category.uploaded_files.push(filePayload);
      filePayload.isLoading = false;
      this.eventService.showSnackBar(`Uploaded ${filePayload.fileType} '${uploadFile.name}' successfully !`, 'success');
    }
    catch (error: any) {
      console.info(error);
      throw error;
    }
  }

  async uploadFile(payload: FileModel, uploadFile: File) {
    try {
      //add thumbnail base64 string length to original file size multiplied by 1.4 (approximation of base64 size)
      const totalBase64Size = (payload.file_thumbnail_body?.length ?? 0) + (uploadFile.size * 1.4);
      //If total payload size including thumbnail in base64 < 6 MB, add file body in API payload
      if (totalBase64Size < this.maxDirectUploadSize) {
        payload.file_body = await this.eventService.encodeToBase64(uploadFile);
      }
      const response = totalBase64Size < this.maxDirectUploadSize ? await this.mfgAppsApiService.uploadFile(payload)
        : await this.mfgAppsApiService.uploadFileLarge(payload);
      return response.result;
    }
    catch (error: any) {
      throw error.message ?? `${payload.file_name} failed to upload, Please try again!`;
    }
  }

  async onDownload(file: FileModel) {
    this.loader.message = `Downloading ${file.fileType} ...`;
    try {
      const fileResult = (await this.mfgAppsApiService.downloadFile(file.id)).result;
      const response = await this.mfgAppsApiService.downloadFileFromPresignedUrl(fileResult.url);
      await this.eventService.simulateAnchorClick(response.body, fileResult.file_name);
      this.eventService.showSnackBar(`Downloaded ${file.fileType} '${file.file_name}' successfully !`, 'success');
    }
    catch (error) {
      console.info(error);
      this.eventService.showSnackBar("Download failed, Please try again !", 'error');
    }
    this.resetLoader();
  }

  async onDelete(category: FileCategoryModel, file: FileModel) {
    this.loader.message = `Deleting ${file.fileType}...`;
    try {
      await this.mfgAppsApiService.deleteFile(file.id);
      this.eventService.showSnackBar(`Deleted ${file.fileType} '${file.file_name}' successfully !`, 'success');
      category.uploaded_files = category.uploaded_files.filter(f => f.id !== file.id);
    }
    catch (error) {
      console.info(error);
      this.eventService.showSnackBar("Delete failed, Please try again !", 'error');
    }
    this.resetLoader();
  }

  openImageInNewTab(file: FileModel) {
    window.open(file.file_thumbnail_URL, '_blank');
  }

  compressImage(file: File, targetSizeKB = 250): Promise<File> {
    return new Promise((resolve, reject) => {
      const currentSizeKB = file.size / 1024;
      if (currentSizeKB <= targetSizeKB) {
        resolve(file);
        return;
      }
      const img = new Image();
      img.src = URL.createObjectURL(file); // Faster than DataURL
      img.onload = async () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
          let blob: Blob;
          const tolerance = 20; // KB tolerance
          let desiredQuality = targetSizeKB / currentSizeKB;
          //Keep reducing quality by 0.1 until target size is met within tolerance, minimum quality 0.2
          while (!blob || desiredQuality >= 0.2) {
            blob = await new Promise<Blob>(result =>
              canvas.toBlob(result, "image/jpeg", desiredQuality)
            );
            const sizeKB = blob.size / 1024;
            console.info(`Compressed Quality: ${desiredQuality.toFixed(2)} , Size: ${sizeKB.toFixed(2)} KB`);
            if (Math.abs(sizeKB - targetSizeKB) <= tolerance) {
              break; // Stop if within target ± tolerance
            }
            desiredQuality = desiredQuality - 0.1;
          }
          let finalFile = new File([blob], file.name, { type: "image/jpeg" });
          // Prevent larger than original
          if (finalFile.size >= file.size) {
            finalFile = file;
          }
          resolve(finalFile);
        }
        catch (error) {
          console.info(error);
          reject("Compression failed");
        }
      };
      img.onerror = () => reject("Image load failed");
    });
  }

  resetLoader() {
    this.loader.isLoading = false;
    this.loader.message = '';
  }
}